// src/scenes/restaurant/Chambres/Chambres.jsx
//
// Configuration des chambres. Aligné sur le modèle actuel :
//   - trois tarifs : `prix_moment` (forfait court), `prix_nuit`, `prix_sejour`
//     (nuit en séjour prolongé) — `prix_heure` n'existe plus
//   - `duree_moment_heures` : durée du forfait moment, elle pilote le décompte
//     et l'alerte de fin de temps côté app réception
//   - `location_moment_active` / `_nuit_` / `_sejour_` : ce que la réception
//     peut proposer sur cette chambre
//   - `statut` est en lecture seule (piloté par les check-in / check-out) ;
//     seule la remise en disponible est possible ici, en cas de blocage.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Switch, FormControlLabel,
  Card, CardContent, CircularProgress,
  Avatar, FormControl, InputLabel, Select,
  InputAdornment, Tab, Tabs, Alert, Tooltip, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Edit, Delete, Add, Bed, Search, Image as ImageIcon,
  Warning, Save, AcUnit, Air, Star, LockOpen,
} from '@mui/icons-material';
import { chambreAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const TYPES = [
  { value: 'ventilateur', label: 'Ventilateur', icon: <Air fontSize="small" /> },
  { value: 'climatiseur', label: 'Climatiseur', icon: <AcUnit fontSize="small" /> },
  { value: 'suite', label: 'Suite', icon: <Star fontSize="small" /> },
];

const STATUTS = {
  disponible: { label: 'Disponible', color: 'success' },
  occupee: { label: 'Occupée', color: 'warning' },
  sale: { label: 'À nettoyer', color: 'info' },
  maintenance: { label: 'En maintenance', color: 'error' },
  reservee: { label: 'Réservée', color: 'secondary' },
};

const FORM_VIDE = {
  id: null,
  numero: '',
  type_chambre: 'ventilateur',
  description: '',
  capacite: 2,
  prix_moment: '',
  prix_nuit: '',
  prix_sejour: '',
  duree_moment_heures: 3,
  location_moment_active: true,
  location_nuit_active: true,
  location_sejour_active: false,
  image_principale: null,
  imagePreview: null,
};

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(n) || 0);

const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.detail) return d.detail;
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    return `${champ} : ${Array.isArray(valeur) ? valeur[0] : valeur}`;
  }
  return defaut;
};

const ChambresPage = () => {
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(FORM_VIDE);

  const fetchChambres = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chambreAPI.getAll();
      setChambres(versTableau(response.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des chambres');
      setError(msg);
      toast.error(msg);
      setChambres([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChambres(); }, [fetchChambres]);

  const handleOpenDialog = (chambre = null) => {
    if (chambre) {
      setFormData({
        id: chambre.id,
        numero: chambre.numero || '',
        type_chambre: chambre.type_chambre || 'ventilateur',
        description: chambre.description || '',
        capacite: chambre.capacite ?? 2,
        prix_moment: chambre.prix_moment ?? '',
        prix_nuit: chambre.prix_nuit ?? '',
        prix_sejour: chambre.prix_sejour ?? '',
        duree_moment_heures: chambre.duree_moment_heures ?? 3,
        location_moment_active: chambre.location_moment_active !== false,
        location_nuit_active: chambre.location_nuit_active !== false,
        location_sejour_active: !!chambre.location_sejour_active,
        image_principale: null,
        imagePreview: chambre.image_principale || null,
      });
      setIsEditing(true);
    } else {
      setFormData(FORM_VIDE);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((f) => ({ ...f, image_principale: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  const construirePayload = () => {
    const champs = {
      numero: formData.numero.trim(),
      type_chambre: formData.type_chambre,
      description: formData.description || '',
      capacite: Number(formData.capacite) || 1,
      prix_moment: Number(formData.prix_moment) || 0,
      prix_nuit: Number(formData.prix_nuit) || 0,
      prix_sejour: Number(formData.prix_sejour) || 0,
      duree_moment_heures: Number(formData.duree_moment_heures) || 0,
      location_moment_active: formData.location_moment_active,
      location_nuit_active: formData.location_nuit_active,
      location_sejour_active: formData.location_sejour_active,
    };

    if (!(formData.image_principale instanceof File)) return champs;

    const fd = new FormData();
    Object.entries(champs).forEach(([cle, valeur]) => fd.append(cle, valeur));
    fd.append('image_principale', formData.image_principale);
    return fd;
  };

  const handleSubmit = async () => {
    if (!formData.numero.trim()) return toast.error('Le numéro de la chambre est requis');
    if (!formData.prix_nuit || Number(formData.prix_nuit) <= 0) {
      return toast.error('Le prix par nuit est requis');
    }
    if (formData.location_moment_active && Number(formData.duree_moment_heures) <= 0) {
      return toast.error('La durée du forfait moment doit être supérieure à 0');
    }
    if (formData.location_moment_active && Number(formData.prix_moment) <= 0) {
      return toast.error('Le prix du moment est requis si le moment est proposé');
    }
    if (formData.location_sejour_active && Number(formData.prix_sejour) <= 0) {
      return toast.error('Le prix séjour est requis si le séjour est proposé');
    }

    setSubmitting(true);
    try {
      const payload = construirePayload();
      if (isEditing) {
        await chambreAPI.update(formData.id, payload);
        toast.success('Chambre modifiée');
      } else {
        await chambreAPI.create(payload);
        toast.success('Chambre créée');
      }
      setOpenDialog(false);
      setFormData(FORM_VIDE);
      fetchChambres();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (chambre) => {
    if (chambre.statut === 'occupee') {
      return toast.error('Impossible de supprimer une chambre occupée');
    }
    if (!window.confirm(`Supprimer la chambre ${chambre.numero} ?`)) return;
    try {
      await chambreAPI.delete(chambre.id);
      toast.success('Chambre supprimée');
      fetchChambres();
    } catch (err) {
      // PROTECT sur les locations : une chambre déjà louée ne peut pas partir
      toast.error(messageErreur(err, 'Suppression impossible — cette chambre a un historique de locations'));
    }
  };

  // Débloquer une chambre restée en « à nettoyer » ou « maintenance ».
  // Une chambre occupée se libère par le check-out, pas d'ici.
  const libererChambre = async (chambre) => {
    if (chambre.statut === 'occupee') {
      return toast.error('Cette chambre est occupée : passez par le check-out');
    }
    try {
      await chambreAPI.marquerDisponible(chambre.id);
      toast.success(`Chambre ${chambre.numero} remise en disponible`);
      fetchChambres();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors de la libération'));
    }
  };

  const filtrees = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return chambres.filter((c) => {
      const correspondRecherche =
        c.numero?.toLowerCase().includes(recherche) ||
        c.description?.toLowerCase().includes(recherche) ||
        false;
      const correspondType = selectedType === 'all' || c.type_chambre === selectedType;
      const correspondStatut =
        activeTab === 0
        || (activeTab === 1 && c.statut === 'disponible')
        || (activeTab === 2 && c.statut === 'occupee');
      return correspondRecherche && correspondType && correspondStatut;
    });
  }, [chambres, searchTerm, selectedType, activeTab]);

  const stats = useMemo(() => {
    const total = chambres.length;
    const occupees = chambres.filter((c) => c.statut === 'occupee').length;
    const prix = chambres.map((c) => Number(c.prix_nuit) || 0).filter((p) => p > 0);
    return {
      total,
      occupees,
      disponibles: chambres.filter((c) => c.statut === 'disponible').length,
      tauxOccupation: total > 0 ? (occupees / total) * 100 : 0,
      prixNuitMoyen: prix.length ? prix.reduce((s, p) => s + p, 0) / prix.length : 0,
    };
  }, [chambres]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchChambres}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <Bed sx={{ mr: 2, verticalAlign: 'middle' }} />
              Chambres
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Tarifs, forfaits et options de location de vos chambres
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher un numéro..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 280 }}
            />
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ height: 40, px: 3 }}>
              Nouvelle chambre
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Total chambres</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Disponibles</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.disponibles}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Taux d'occupation</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.tauxOccupation.toFixed(0)} %
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {stats.occupees} occupée(s)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Prix nuit moyen</Typography>
                <Typography variant="h4" fontWeight="bold">{fmt(stats.prixNuitMoyen)}</Typography>
                <Typography variant="caption" color="textSecondary">HTG</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Type de chambre</InputLabel>
          <Select value={selectedType} label="Type de chambre" onChange={(e) => setSelectedType(e.target.value)}>
            <MenuItem value="all">Tous les types</MenuItem>
            {TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minHeight: 48 } }}>
          <Tab label="Toutes" />
          <Tab label="Disponibles" />
          <Tab label="Occupées" />
        </Tabs>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Numéro</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Capacité</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Moment</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Nuit</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Séjour</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm || selectedType !== 'all'
                        ? 'Aucune chambre ne correspond aux critères'
                        : 'Aucune chambre configurée'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtrees.map((chambre) => {
                  const statut = STATUTS[chambre.statut] || { label: chambre.statut, color: 'default' };
                  const type = TYPES.find((t) => t.value === chambre.type_chambre);
                  return (
                    <TableRow key={chambre.id} hover>
                      <TableCell>
                        <Avatar
                          src={chambre.image_principale || undefined}
                          variant="rounded"
                          sx={{ width: 56, height: 56, backgroundColor: 'action.hover' }}
                        >
                          {!chambre.image_principale && <Bed />}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{chambre.numero}</Typography>
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 180 }}>
                          {chambre.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {type?.icon}
                          <Typography variant="body2">{type?.label || chambre.type_chambre}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{chambre.capacite}</TableCell>
                      <TableCell align="right">
                        {chambre.location_moment_active ? (
                          <Box>
                            <Typography fontWeight="medium">{fmt(chambre.prix_moment)}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {chambre.duree_moment_heures} h
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {chambre.location_nuit_active ? (
                          <Typography fontWeight="medium">{fmt(chambre.prix_nuit)}</Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {chambre.location_sejour_active ? (
                          <Typography fontWeight="medium">{fmt(chambre.prix_sejour)}</Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={statut.label} size="small" color={statut.color} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {chambre.statut !== 'disponible' && chambre.statut !== 'occupee' && (
                            <Tooltip title="Remettre en disponible">
                              <IconButton color="success" size="small" onClick={() => libererChambre(chambre)}>
                                <LockOpen />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton color="primary" onClick={() => handleOpenDialog(chambre)} size="small">
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(chambre)}
                            size="small"
                            disabled={chambre.statut === 'occupee'}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Création / modification */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Bed />
            <Typography variant="h6">{isEditing ? 'Modifier la chambre' : 'Nouvelle chambre'}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* ── Identité ────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">Identité</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Numéro"
                fullWidth
                size="small"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                required
                error={!formData.numero.trim()}
                helperText={!formData.numero.trim() ? 'Ce champ est requis' : 'Ex : 101, 102, 201'}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de chambre</InputLabel>
                <Select
                  value={formData.type_chambre}
                  label="Type de chambre"
                  onChange={(e) => setFormData({ ...formData, type_chambre: e.target.value })}
                  disabled={submitting}
                >
                  {TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Capacité"
                type="number"
                fullWidth
                size="small"
                value={formData.capacite}
                onChange={(e) => setFormData({ ...formData, capacite: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">pers.</InputAdornment>,
                  inputProps: { step: '1', min: '1' },
                }}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Vue sur jardin, salle de bain privée..."
                disabled={submitting}
              />
            </Grid>

            {/* ── Formules ────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Formules et tarifs</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Seules les formules activées sont proposées à la réception.
              </Typography>
            </Grid>

            {/* Moment */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.location_moment_active}
                      onChange={(e) => setFormData({ ...formData, location_moment_active: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Moment — forfait court</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Payé à l'entrée. La durée déclenche le décompte et l'alerte de fin de temps.
                      </Typography>
                    </Box>
                  }
                />
                {formData.location_moment_active && (
                  <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                    <TextField
                      label="Prix du moment"
                      type="number"
                      size="small"
                      value={formData.prix_moment}
                      onChange={(e) => setFormData({ ...formData, prix_moment: e.target.value })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                        inputProps: { step: '0.01', min: '0' },
                      }}
                      disabled={submitting}
                      sx={{ width: 200 }}
                    />
                    <TextField
                      label="Durée du forfait"
                      type="number"
                      size="small"
                      value={formData.duree_moment_heures}
                      onChange={(e) => setFormData({ ...formData, duree_moment_heures: e.target.value })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">heures</InputAdornment>,
                        inputProps: { step: '0.5', min: '0.5' },
                      }}
                      disabled={submitting}
                      sx={{ width: 200 }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Nuit */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.location_nuit_active}
                      onChange={(e) => setFormData({ ...formData, location_nuit_active: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={<Typography variant="body2">Nuit</Typography>}
                />
                <TextField
                  label="Prix par nuit"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.prix_nuit}
                  onChange={(e) => setFormData({ ...formData, prix_nuit: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                    inputProps: { step: '0.01', min: '0' },
                  }}
                  required
                  error={!formData.prix_nuit || Number(formData.prix_nuit) <= 0}
                  helperText={
                    !formData.prix_nuit || Number(formData.prix_nuit) <= 0
                      ? 'Requis — sert de tarif de référence'
                      : ' '
                  }
                  disabled={submitting}
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>

            {/* Séjour */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.location_sejour_active}
                      onChange={(e) => setFormData({ ...formData, location_sejour_active: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label={<Typography variant="body2">Séjour prolongé</Typography>}
                />
                <TextField
                  label="Prix par nuit en séjour"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.prix_sejour}
                  onChange={(e) => setFormData({ ...formData, prix_sejour: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                    inputProps: { step: '0.01', min: '0' },
                  }}
                  helperText="Tarif dégressif à partir de 2 nuits"
                  disabled={submitting || !formData.location_sejour_active}
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>

            {/* ── Visuel ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Visuel</Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={formData.imagePreview || undefined}
                  variant="rounded"
                  sx={{ width: 72, height: 72, border: '1px solid', borderColor: 'divider' }}
                >
                  <Bed />
                </Avatar>
                <Box>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />} disabled={submitting}>
                    Choisir une image
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} disabled={submitting} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Optionnel
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {isEditing && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Le statut de la chambre est piloté par les check-in et check-out de la réception :
                  il ne se modifie pas ici.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => !submitting && setOpenDialog(false)} variant="outlined" disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.numero.trim() || !formData.prix_nuit}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChambresPage;
